import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Container, IconButton, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { titleToUrl } from "../../imports/generalImport";

const YouMightBeInterested = ({ pageLoading, giftCardData = [] }) => {
  const theme = useTheme();

  // 👇 Screen size ke hisaab se visible slides count
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    dragFree: false,
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, giftCardData]);

  const products = useMemo(
    () => (Array.isArray(giftCardData) ? giftCardData.filter(Boolean) : []),
    [giftCardData]
  );

  // Chunk into pairs of 2 (vertical stack per slide)
  const chunkedProducts = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < products.length; i += 2) {
      chunks.push(products.slice(i, i + 2));
    }
    return chunks;
  }, [products]);

  const getImage = (item) =>
    item?.imageUrl ||
    item?.image ||
    item?.productImage ||
    item?.thumbnail ||
    item?.images?.[0]?.url ||
    item?.images?.[0] ||
    item?.media?.[0]?.url ||
    item?.media?.[0] ||
    "";

  const getName = (item) =>
    item?.name ||
    item?.productName ||
    item?.title ||
    item?.product?.name ||
    "Gift Card";

  const getDiscount = (item) => {
    const discount =
      item?.discount ??
      item?.discountPercentage ??
      item?.discountPercent ??
      item?.discountValue ??
      item?.product?.discount ??
      0;
    return discount === null || discount === undefined || discount === ""
      ? "0"
      : discount;
  };

  const getProductUrl = (item) => {
    const id = item?.id || item?.productId || item?.sku;
    if (!id) return "#";
    return `/card-details/${id}/${titleToUrl(item?.title || item?.name || "")}${
      item?.url_utm ? `?${item.url_utm}` : ""
    }`;
  };

  // ---------- RESPONSIVE CONFIG ----------
  const gapSx = {
    xs: "8px",
    sm: "10px",
    md: "12px",
    lg: "13px",
    xl: "13px",
  };

  const slideWidth = {
    xs: "0 0 calc(25% - 6px)",
    sm: "0 0 calc(25% - 7.5px)",
    md: "0 0 calc(25% - 9px)",
    lg: "0 0 calc(19.8% - 9.8px)",
    xl: "0 0 calc(20% - 10px)",
  };

  // 👇 Visible slides count (breakpoint ke hisaab se)
  const visibleSlides = useMemo(() => {
    if (isXs) return 4;    // xs → 4 cards
    if (isSm) return 4;    // sm → 4 cards
    if (isMd) return 4;    // md → 4 cards
    if (isLg) return 5;    // lg/xl → 5 cards
    return 5;
  }, [isXs, isSm, isMd, isLg]);

  // 👇 Arrow show/hide condition
  const totalSlides = chunkedProducts.length;
  const shouldShowArrows = totalSlides > visibleSlides;

  // ---------- LOADING SKELETON ----------
  if (pageLoading) {
    return (
      <Box sx={{ width: "100%", pb: { xs: 3, sm: 4, md: 5 } }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{ px: { xs: 1, sm: 2, md: 3, lg: 0 } }}
        >
          <Box sx={{ mb: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 0 } }}>
            <Skeleton
              animation="wave"
              sx={{
                width: { xs: "200px", sm: "280px", md: "320px", xl: "350px" },
                height: "35px",
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: gapSx,
              overflow: "hidden",
              px: { xs: 1, sm: 0 },
            }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: slideWidth,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: {
                    xs: "10px",
                    sm: "12px",
                    md: "14px",
                    lg: "18px",
                    xl: "20px",
                  },
                }}
              >
                {[0, 1].map((cardIdx) => (
                  <Box key={cardIdx}>
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "100%",
                        aspectRatio: "1.52 / 1",
                        borderRadius: "12px",
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: "-10px",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      <Skeleton
                        animation="wave"
                        sx={{
                          width: { xs: "70px", sm: "80px", md: "95px", xl: "105px" },
                          height: "24px",
                        }}
                      />
                    </Box>
                    <Skeleton
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "21px",
                        mx: "auto",
                        mt: 1,
                      }}
                    />
                    <Skeleton
                      animation="wave"
                      sx={{ width: "65%", height: "21px", mx: "auto" }}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  // ---------- MAIN RENDER ----------
  return (
    <Box sx={{ width: "100%", pb: { xs: 3, sm: 4, md: 5 } }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 } }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: {
              xs: "15px",
              sm: "17px",
              md: "19px",
              lg: "18px",
              xl: "18px",
            },
            fontWeight: 400,
            color: "text.primary",
            textAlign: { xs: "center", sm: "left" },
            textTransform: "capitalize",
            mb: { xs: 1.5, sm: 2 },
            px: { xs: 1, sm: 0 },
          }}
        >
          You might be interested in
        </Typography>

        <Box sx={{ width: "100%", position: "relative" }}>
          <Box
            ref={emblaRef}
            sx={{
              width: "100%",
              overflow: "hidden",
              px: { xs: "4px", sm: "0px" },
            }}
          >
            <Box sx={{ display: "flex", gap: gapSx }}>
              {chunkedProducts.map((pair, index) => (
                <Box
                  key={`slide-${index}`}
                  sx={{
                    flex: slideWidth,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: {
                      xs: "10px",
                      sm: "12px",
                      md: "14px",
                      lg: "18px",
                      xl: "20px",
                    },
                  }}
                >
                  {pair.map((item, itemIndex) => {
                    const image = getImage(item);
                    const name = getName(item);
                    const discount = getDiscount(item);

                    return (
                      <Box
                        key={
                          item?.id ||
                          item?.productId ||
                          item?.sku ||
                          `gift-card-${index}-${itemIndex}`
                        }
                        component={Link}
                        to={getProductUrl(item)}
                        sx={{
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                          "&:hover img": { transform: "scale(1.02)" },
                        }}
                      >
                        {/* IMAGE */}
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1.52 / 1",
                            borderRadius: {
                              xs: "8px",
                              sm: "10px",
                              md: "11px",
                              lg: "12px",
                              xl: "12px",
                            },
                            overflow: "hidden",
                            backgroundColor: "#f5f5f5",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.20)",
                            position: "relative",
                          }}
                        >
                          {image ? (
                            <Box
                              component="img"
                              src={image}
                              alt={name}
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                                objectFit: "cover",
                                objectPosition: "center",
                                transition: "transform 0.25s ease",
                              }}
                            />
                          ) : (
                            <Skeleton
                              variant="rectangular"
                              animation="wave"
                              sx={{ width: "100%", height: "100%" }}
                            />
                          )}
                        </Box>

                        {/* DISCOUNT BADGE */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: { xs: "-8px", sm: "-9px", md: "-10px" },
                            position: "relative",
                            zIndex: 5,
                            pointerEvents: "none",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              backgroundColor: "#24b7ad",
                              color: "#fff",
                              fontSize: {
                                xs: "8px",
                                sm: "9px",
                                md: "11px",
                                lg: "12px",
                                xl: "13px",
                              },
                              fontWeight: 500,
                              lineHeight: "18px",
                              height: {
                                xs: "18px",
                                sm: "20px",
                                md: "22px",
                                lg: "23px",
                                xl: "24px",
                              },
                              minWidth: {
                                xs: "70px",
                                sm: "80px",
                                md: "95px",
                                lg: "100px",
                                xl: "105px",
                              },
                              px: { xs: "6px", sm: "8px", md: "10px" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              whiteSpace: "nowrap",
                              clipPath:
                                "polygon(4% 0%, 96% 0%, 100% 50%, 96% 100%, 4% 100%, 0% 50%)",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                            }}
                          >
                            Discount : {discount}%
                          </Box>
                        </Box>

                        {/* NAME */}
                        <Typography
                          sx={{
                            mt: {
                              xs: "5px",
                              sm: "6px",
                              md: "8px",
                              lg: "9px",
                              xl: "9px",
                            },
                            px: {
                              xs: "3px",
                              sm: "4px",
                              md: "5px",
                            },
                            color: "#002865",
                            fontSize: {
                              xs: "10px",
                              sm: "11px",
                              md: "13px",
                              lg: "14px",
                              xl: "15px",
                            },
                            fontWeight: 400,
                            lineHeight: {
                              xs: "14px",
                              sm: "15px",
                              md: "17px",
                              lg: "19px",
                              xl: "20px",
                            },
                            textAlign: "center",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minHeight: {
                              xs: "28px",
                              sm: "30px",
                              md: "34px",
                              lg: "38px",
                              xl: "40px",
                            },
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>

          {/* 👇 LEFT ARROW — conditionally render */}
          {shouldShowArrows && (
            <IconButton
              onClick={scrollPrev}
              aria-label="Previous products"
              sx={{
                position: "absolute",
                left: {
                  xs: "2px",
                  sm: "-10px",
                  md: "-14px",
                  lg: "-16px",
                  xl: "-18px",
                },
                top: "45%",
                transform: "translateY(-50%)",
                zIndex: 20,
                width: { xs: 24, sm: 28, md: 32, lg: 36, xl: 38 },
                height: { xs: 24, sm: 28, md: 32, lg: 36, xl: 38 },
                backgroundColor: "#fff",
                color: "primary.main",
                boxShadow: "0 2px 7px rgba(0,0,0,0.22)",
                border: "1px solid #eeeeee",
                display: "flex",
                "&:hover": {
                  backgroundColor: "#fff",
                  boxShadow: "0 3px 9px rgba(0,0,0,0.28)",
                },
              }}
            >
              <ArrowBackIosNewIcon
                sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px", xl: "18px" } }}
              />
            </IconButton>
          )}

          {/* 👇 RIGHT ARROW — conditionally render */}
          {shouldShowArrows && (
            <IconButton
              onClick={scrollNext}
              aria-label="Next products"
              sx={{
                position: "absolute",
                right: {
                  xs: "2px",
                  sm: "-10px",
                  md: "-14px",
                  lg: "-16px",
                  xl: "-18px",
                },
                top: "45%",
                transform: "translateY(-50%)",
                zIndex: 20,
                width: { xs: 24, sm: 28, md: 32, lg: 36, xl: 38 },
                height: { xs: 24, sm: 28, md: 32, lg: 36, xl: 38 },
                backgroundColor: "#fff",
                color: "primary.main",
                boxShadow: "0 2px 7px rgba(0,0,0,0.22)",
                border: "1px solid #eeeeee",
                display: "flex",
                "&:hover": {
                  backgroundColor: "#fff",
                  boxShadow: "0 3px 9px rgba(0,0,0,0.28)",
                },
              }}
            >
              <ArrowForwardIosIcon
                sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px", xl: "18px" } }}
              />
            </IconButton>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default YouMightBeInterested;